import React, { useState } from "react";
import { formatMessage } from "../../../lib/globalize-util";
import {
    TCustomizationSelection,
    CustomizationSelectionType,
} from "constants/product-customizations/customization-selection.types";
import { formatColorValue, isValidHexColor } from "../../product/attributes";
import { makeStyles, Popover } from "@material-ui/core";
import { CUSTOMIZATIONS } from "constants/integration/customizations";
import { DATATEST } from "constants/integration";

interface IProps {
    customization: TCustomizationSelection;
}

export const CustomizationViewContent: React.FC<IProps> = ({ customization }) => {
    const classes = useStyles();
    const { type, displayValue, value, printType, nestedValues } = customization;
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const handlePopoverOpen = (event: React.MouseEvent<HTMLElement, MouseEvent>) => setAnchorEl(event.currentTarget);
    const handlePopoverClose = () => setAnchorEl(null);

    const printTypeElement = printType ? (
        <div className={classes.customizationValue} data-test={CUSTOMIZATIONS.CUSTOMIZATION_PRINT_TYPE}>
            <b data-test={CUSTOMIZATIONS.CUSTOMIZATION_PRINT_TYPE_LABEL}>{formatMessage("Print Type")}:</b>{" "}
            <span data-test={CUSTOMIZATIONS.CUSTOMIZATION_PRINT_TYPE_VALUE}>{printType}</span>
        </div>
    ) : null;

    switch (type) {
        case CustomizationSelectionType.COLOR_PICKER:
        case CustomizationSelectionType.COLOR_SWATCH: {
            return (
                <div data-test={CUSTOMIZATIONS.CUSTOMIZATION_COLOR}>
                    {formatColorValue(value, displayValue || value)}
                </div>
            );
        }
        case CustomizationSelectionType.PRINTED_IMAGE: {
            return (
                <>
                    {printTypeElement}
                    {nestedValues.map((value, i) => (
                        <div
                            key={i}
                            className={classes.customizationValue}
                            data-test={`${CUSTOMIZATIONS.CHILD_ATTRIBUTE}_${i}`}
                        >
                            <b data-test={CUSTOMIZATIONS.PRODUCT_CUSTOMIZATION_LABEL}>{`${value.label}: `}</b>
                            <span
                                data-test={CUSTOMIZATIONS.PRODUCT_CUSTOMIZATION_VALUE}
                            >{`${value.displayValue}`}</span>
                        </div>
                    ))}
                    <img
                        onMouseEnter={handlePopoverOpen}
                        onMouseLeave={handlePopoverClose}
                        data-test={CUSTOMIZATIONS.CUSTOMIZATION_LOGO_IMAGE}
                        className={classes.logo}
                        src={displayValue}
                    />
                    <Popover
                        style={{ pointerEvents: "none" }}
                        className={classes.existingImagePopover}
                        open={Boolean(anchorEl)}
                        anchorEl={anchorEl}
                        anchorOrigin={{
                            vertical: "top",
                            horizontal: "left",
                        }}
                        transformOrigin={{
                            vertical: "bottom",
                            horizontal: "right",
                        }}
                        onClose={handlePopoverClose}
                        disableRestoreFocus
                    >
                        <div className={classes.popoverContentWrapper}>
                            <div
                                data-test={DATATEST.CUSTOMIZATIONS.POPOVER_IMAGE}
                                className={classes.popoverImage}
                                style={{ backgroundImage: `url("${displayValue}")` }}
                            />
                        </div>
                    </Popover>
                </>
            );
        }
        case CustomizationSelectionType.PRINTED_TEXT: {
            const color = nestedValues?.find((val) => val.valueKey === "colorPicker" || val.valueKey === "colorSwatch");
            const font = nestedValues?.find((val) => val.valueKey === "font");
            const fontSize = nestedValues?.find((val) => val.valueKey === "fontSize");
            const colorValid = color && isValidHexColor(color.value);

            return (
                <>
                    <div
                        className={classes.customizationValue}
                        data-test={CUSTOMIZATIONS.CUSTOMIZATION_PRINTED_TEXT_TEXT}
                    >
                        <b data-test={CUSTOMIZATIONS.PRODUCT_CUSTOMIZATION_LABEL}>Text: </b>
                        <span data-test={CUSTOMIZATIONS.PRODUCT_CUSTOMIZATION_VALUE}>{displayValue || value}</span>
                    </div>
                    {printTypeElement}
                    {colorValid && (
                        <>
                            {color && (
                                <div className={classes.colorView} data-test={CUSTOMIZATIONS.CUSTOMIZATION_COLOR}>
                                    <span className={classes.colorTitle}>
                                        <b data-test={CUSTOMIZATIONS.PRODUCT_CUSTOMIZATION_LABEL}>
                                            {formatMessage("Color")}:
                                        </b>
                                    </span>
                                    <span
                                        className={classes.customizationValue}
                                        data-test={CUSTOMIZATIONS.PRODUCT_CUSTOMIZATION_VALUE}
                                    >
                                        {formatColorValue(color.value, color.displayValue || color.value)}
                                    </span>
                                </div>
                            )}
                            {font && (
                                <div
                                    className={classes.customizationValue}
                                    data-test={CUSTOMIZATIONS.CUSTOMIZATION_PRINTED_TEXT_FONT}
                                >
                                    <span>
                                        <b data-test={CUSTOMIZATIONS.PRODUCT_CUSTOMIZATION_LABEL}>
                                            {formatMessage("Font")}:{" "}
                                        </b>
                                    </span>
                                    <span data-test={CUSTOMIZATIONS.PRODUCT_CUSTOMIZATION_VALUE}>
                                        {font.displayValue}
                                    </span>
                                </div>
                            )}
                            {fontSize && (
                                <div
                                    className={classes.customizationValue}
                                    data-test={CUSTOMIZATIONS.CUSTOMIZATION_PRINTED_TEXT_FONT_SIZE}
                                >
                                    <span>
                                        <b data-test={CUSTOMIZATIONS.PRODUCT_CUSTOMIZATION_LABEL}>
                                            {formatMessage("Font size")}:{" "}
                                        </b>
                                    </span>
                                    <span data-test={CUSTOMIZATIONS.PRODUCT_CUSTOMIZATION_VALUE}>
                                        {fontSize.displayValue}
                                    </span>
                                </div>
                            )}
                        </>
                    )}
                </>
            );
        }
        case CustomizationSelectionType.SELECTABLE:
        default: {
            return (
                <div className="value" data-test={CUSTOMIZATIONS.PRODUCT_CUSTOMIZATION_VALUE}>
                    {displayValue || value}
                </div>
            );
        }
    }
};

const useStyles = makeStyles({
    colorView: { display: "flex", marginRight: "5px" },
    logo: { maxWidth: "60px" },
    colorTitle: { marginRight: "5px" },
    customizationValue: { marginRight: "10px" },
    showLogoButton: {
        cursor: "pointer",
        marginLeft: "10px",
        marginRight: "10px",
        textDecoration: "underline",
    },
    existingImagePopover: {
        "&.MuiPopover-root": {
            zIndex: "1900 !important",
        },
    },
    popoverContentWrapper: { padding: 12 },
    popoverImage: {
        width: 300,
        height: 300,
        border: "1px solid #e1e1e1",
        backgroundSize: "contain",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center",
    },
    popoverImageName: {
        marginTop: 8,
        fontSize: 14,
        fontWeight: "bold",
        textAlign: "center",
    },
});
